/**
 * This browser's own view of a traced meeting, sent to the SFU every few
 * seconds while the SFU asks for it.
 *
 * The SFU can say what it FORWARDED to a viewer; only the viewer's browser can
 * say whether that turned into pictures and sound — frames decoded and frozen,
 * audio the decoder had to invent — and only the sender's browser can say why
 * its encoder is holding back (CPU or bandwidth). The 2026-10-26 dev meetings
 * needed both: a camera that kept going to 0 bps while the same person's mic
 * was fine, and tiles nobody could tell were frozen or merely slow.
 *
 * Off unless asked. The SFU's `hello` carries `clientStatsMs` only while it
 * runs MEDIA_FLOW_LOG (video_conference/src/ws/ws-server.js), so a normal
 * meeting never collects or sends any of this. When on, it reads every stream
 * `rtpStats` knows about — on its own timer, at a fifth of the panel poller's
 * rate, without starting that poller — and sends one compact summary.
 *
 * Every rate is over the interval since the previous report, never since the
 * call began: a tile that froze a minute ago still looks fine by totals.
 */
import { rtpStats, type Registration } from './rtp-stats'
import { normalizeReason } from './quality-limitation'

/** One stream, in the shortest keys that stay readable in a log line. */
export type FlowStream = {
  /** The rtpStats key: `<peerId>:<sourceType>:<kind>`, or `local:...` for what we send. */
  k: string
  d: 'in' | 'out'
  kbps?: number
  /** 0..1 of packets lost over the interval (inbound: ours; outbound: the far end's report). */
  loss?: number
  /** Inbound video: frames per second the decoder actually produced. */
  dec?: number
  /** Inbound video: frames dropped, freezes, and time frozen, over the interval. */
  drop?: number
  frz?: number
  frzMs?: number
  w?: number
  h?: number
  /** Inbound: average jitter-buffer delay over the interval, ms. */
  jbMs?: number
  /** Inbound audio: 0..1 of samples the decoder concealed over the interval. */
  conc?: number
  /** Keyframe requests and NACKs we sent (inbound) or received (outbound), over the interval. */
  pli?: number
  nack?: number
  /** Outbound video: why the encoder is limited right now, and per-layer output. */
  ql?: string
  enc?: { rid?: string; kbps?: number; fps?: number; w?: number; h?: number }[]
  rttMs?: number
}

export type ClientFlowReport = {
  v: 1
  at: number
  streams: FlowStream[]
  /** The selected ICE pair: how this browser actually reaches the SFU. */
  net?: { rttMs?: number; outKbps?: number; local?: string; proto?: string; relay?: string }
}

/** Above this the report is cut, worst-first ordering not attempted: it is a trace, not a census. */
const MAX_STREAMS = 60

type Counters = Record<string, number>
const previous = new Map<string, { at: number; c: Counters }>()

const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined)
const round = (v: number, places = 0) => {
  const f = 10 ** places
  return Math.round(v * f) / f
}

/** Counter deltas since the last report for this key; null on the first. */
function deltas(key: string, at: number, c: Counters): { dt: number; d: Counters } | null {
  const prev = previous.get(key)
  previous.set(key, { at, c })
  if (!prev) return null
  const dt = (at - prev.at) / 1000
  if (dt <= 0) return null
  const d: Counters = {}
  for (const name of Object.keys(c)) d[name] = Math.max(0, c[name] - (prev.c[name] ?? 0))
  return { dt, d }
}

function inbound(id: string, reg: Registration, stat: Record<string, unknown>, at: number): FlowStream {
  const c: Counters = {
    bytes: num(stat.bytesReceived) ?? 0,
    recv: num(stat.packetsReceived) ?? 0,
    lost: num(stat.packetsLost) ?? 0,
    dec: num(stat.framesDecoded) ?? 0,
    drop: num(stat.framesDropped) ?? 0,
    frz: num(stat.freezeCount) ?? 0,
    frzS: num(stat.totalFreezesDuration) ?? 0,
    jbd: num(stat.jitterBufferDelay) ?? 0,
    jbe: num(stat.jitterBufferEmittedCount) ?? 0,
    conc: num(stat.concealedSamples) ?? 0,
    samples: num(stat.totalSamplesReceived) ?? 0,
    pli: num(stat.pliCount) ?? 0,
    nack: num(stat.nackCount) ?? 0,
  }
  const out: FlowStream = { k: id, d: 'in' }
  if (reg.kind === 'video') {
    out.w = num(stat.frameWidth)
    out.h = num(stat.frameHeight)
  }
  const r = deltas(`${id}:${stat.ssrc ?? 'single'}`, at, c)
  if (!r) return out
  const { dt, d } = r
  out.kbps = round((d.bytes * 8) / dt / 1000)
  const sent = d.recv + d.lost
  out.loss = sent > 0 ? round(d.lost / sent, 3) : 0
  if (d.jbe > 0) out.jbMs = round((d.jbd / d.jbe) * 1000)
  out.pli = d.pli
  out.nack = d.nack
  if (reg.kind === 'video') {
    out.dec = round(d.dec / dt, 1)
    out.drop = d.drop
    out.frz = d.frz
    out.frzMs = round(d.frzS * 1000)
  } else if (d.samples > 0) {
    out.conc = round(d.conc / d.samples, 3)
  }
  return out
}

function outbound(id: string, rows: Record<string, unknown>[], remote: Record<string, unknown>[], at: number): FlowStream {
  const out: FlowStream = { k: id, d: 'out', enc: [] }
  let kbps = 0
  let pli = 0
  let nack = 0
  const reasons: string[] = []
  for (const stat of rows) {
    const rid = stat.rid as string | undefined
    const r = deltas(`${id}:${stat.ssrc ?? rid ?? 'single'}`, at, {
      bytes: num(stat.bytesSent) ?? 0,
      pli: num(stat.pliCount) ?? 0,
      nack: num(stat.nackCount) ?? 0,
    })
    const layer = {
      rid,
      kbps: r ? round((r.d.bytes * 8) / r.dt / 1000) : undefined,
      fps: num(stat.framesPerSecond),
      w: num(stat.frameWidth),
      h: num(stat.frameHeight),
    }
    if (r) {
      kbps += layer.kbps ?? 0
      pli += r.d.pli
      nack += r.d.nack
    }
    if (stat.qualityLimitationReason !== undefined) reasons.push(normalizeReason(stat.qualityLimitationReason))
    out.enc!.push(layer)
  }
  out.kbps = kbps
  out.pli = pli
  out.nack = nack
  // The worst reason across layers: the top rung CPU-limited while the
  // thumbnail is fine is the usual shape of trouble.
  const order = ['cpu', 'bandwidth', 'other', 'none']
  if (reasons.length) out.ql = reasons.sort((a, b) => order.indexOf(a) - order.indexOf(b))[0]
  const losses = remote.map((s) => num(s.fractionLost)).filter((v): v is number => v !== undefined)
  const rtts = remote.map((s) => num(s.roundTripTime)).filter((v): v is number => v !== undefined)
  if (losses.length) out.loss = round(Math.max(...losses), 3)
  if (rtts.length) out.rttMs = round(Math.max(...rtts) * 1000)
  if (out.enc!.length <= 1) {
    // Audio, or a single-encoding video: the per-layer list adds nothing.
    const only = out.enc![0]
    delete out.enc
    if (only?.w) { out.w = only.w; out.h = only.h }
  }
  return out
}

/** Read every registered stream once and build the summary. */
export async function collectClientFlow(
  entries: [string, Registration][] = rtpStats.entries(),
  now: () => number = Date.now,
): Promise<ClientFlowReport> {
  const report: ClientFlowReport = { v: 1, at: now(), streams: [] }
  for (const [id, reg] of entries.slice(0, MAX_STREAMS)) {
    let stats: RTCStatsReport
    try {
      stats = await reg.getStats()
    } catch {
      continue // closed between listing and reading
    }
    const at = now()
    const wanted = reg.direction === 'inbound' ? 'inbound-rtp' : 'outbound-rtp'
    const rows: Record<string, unknown>[] = []
    const remote: Record<string, unknown>[] = []
    const byId = new Map<string, Record<string, unknown>>()
    let pair: Record<string, unknown> | undefined
    stats.forEach((entry) => {
      const stat = entry as Record<string, unknown>
      byId.set(stat.id as string, stat)
      if (stat.type === 'candidate-pair' && (stat.nominated === true || stat.selected === true)) pair = stat
      if ((stat.kind as string) !== reg.kind) return
      if (stat.type === wanted) rows.push(stat)
      else if (stat.type === 'remote-inbound-rtp' && reg.direction === 'outbound') remote.push(stat)
    })
    if (reg.direction === 'inbound') {
      for (const stat of rows) report.streams.push(inbound(id, reg, stat, at))
    } else if (rows.length) {
      report.streams.push(outbound(id, rows, remote, at))
    }
    // Every stream rides the same transport pair per direction; the first one
    // seen is enough to say how this browser reaches the SFU.
    if (pair && !report.net) {
      const local = byId.get(pair.localCandidateId as string)
      report.net = {
        rttMs: num(pair.currentRoundTripTime) !== undefined ? round((pair.currentRoundTripTime as number) * 1000) : undefined,
        outKbps: num(pair.availableOutgoingBitrate) !== undefined ? round((pair.availableOutgoingBitrate as number) / 1000) : undefined,
        local: local?.candidateType as string | undefined,
        proto: local?.protocol as string | undefined,
        relay: local?.relayProtocol as string | undefined,
      }
    }
  }
  return report
}

/**
 * Send a summary every `intervalMs` until the returned stop is called. A slow
 * collection skips a beat rather than overlapping, and nothing here can throw
 * into the meeting.
 */
export function startClientFlowReport(
  send: (report: ClientFlowReport) => Promise<unknown>,
  intervalMs: number,
  collect: () => Promise<ClientFlowReport> = () => collectClientFlow(),
): () => void {
  let stopped = false
  let busy = false
  const tick = async () => {
    if (busy || stopped) return
    busy = true
    try {
      const report = await collect()
      if (!stopped && report.streams.length) await send(report)
    } catch {
      // A diagnostic must never be the thing that breaks a call.
    } finally {
      busy = false
    }
  }
  const timer = setInterval(tick, Math.max(2000, intervalMs))
  return () => {
    stopped = true
    clearInterval(timer)
    previous.clear()
  }
}
